import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowRight, Phone } from "lucide-react";
import { SERVICES } from "../data/services";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSolutionsOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || mobileOpen
          ? "border-b border-white/10 bg-black/60 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 md:px-10">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
          <img
            src="/logo.jpg"
            alt="Logo Resineko"
            className="h-11 w-11 rounded-full object-cover ring-1 ring-[#d4af6a]/40 transition-transform duration-500 group-hover:rotate-[8deg]"
          />
          <span className="flex flex-col leading-none">
            <span className="font-serif text-xl font-medium tracking-[0.18em] text-[#F4F4F0]">RESINEKO</span>
            <span className="mt-1 hidden text-[9px] uppercase tracking-[0.3em] text-[#2FBF8F] sm:block">L'art de sublimer vos espaces</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Navigation principale">
          <Link to="/" data-testid="nav-accueil" className="text-sm text-[#F4F4F0]/80 transition-colors hover:text-[#F4F4F0]">
            Accueil
          </Link>
          <div
            className="relative"
            onMouseEnter={() => setSolutionsOpen(true)}
            onMouseLeave={() => setSolutionsOpen(false)}
          >
            <button
              data-testid="nav-solutions-trigger"
              className="flex items-center gap-1.5 text-sm text-[#F4F4F0]/80 transition-colors hover:text-[#F4F4F0]"
              onClick={() => setSolutionsOpen(!solutionsOpen)}
              aria-expanded={solutionsOpen}
            >
              Nos solutions
              <ChevronDown size={14} className={`transition-transform duration-300 ${solutionsOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {solutionsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute left-1/2 top-full mt-4 w-[540px] -translate-x-1/2 border border-white/10 bg-[#111111]/95 p-6 backdrop-blur-xl"
                  data-testid="nav-solutions-menu"
                >
                  <div className="grid grid-cols-2 gap-1">
                    {SERVICES.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/${s.slug}`}
                        data-testid={`nav-solution-${s.slug}`}
                        className="group/item px-4 py-3 transition-colors hover:bg-white/5"
                      >
                        <span className="block font-serif text-lg text-[#F4F4F0]">{s.name}</span>
                        <span className="mt-0.5 block text-xs leading-relaxed text-[#8C8C8C]">{s.tagline}</span>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <Link to="/realisations" data-testid="nav-realisations" className="text-sm text-[#F4F4F0]/80 transition-colors hover:text-[#F4F4F0]">
            Réalisations
          </Link>
          <Link to="/zone-intervention" data-testid="nav-zone" className="text-sm text-[#F4F4F0]/80 transition-colors hover:text-[#F4F4F0]">
            Zone d'intervention
          </Link>
          <Link to="/blog" data-testid="nav-blog" className="text-sm text-[#F4F4F0]/80 transition-colors hover:text-[#F4F4F0]">
            Blog
          </Link>
          <Link to="/a-propos" data-testid="nav-apropos" className="text-sm text-[#F4F4F0]/80 transition-colors hover:text-[#F4F4F0]">
            À propos
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="tel:+33783693101"
            data-testid="nav-phone"
            className="hidden items-center gap-2 whitespace-nowrap text-sm text-[#F4F4F0]/80 transition-colors hover:text-[#2FBF8F] md:flex"
            aria-label="Appeler Resineko"
          >
            <Phone size={15} className="text-[#2FBF8F]" />
            <span className="hidden xl:inline">07 83 69 31 01</span>
          </a>
          <Link
            to="/devis"
            data-testid="nav-devis-cta"
            className="group relative hidden overflow-hidden border border-[#047857] px-5 py-2.5 text-xs font-medium uppercase tracking-[0.18em] text-[#F4F4F0] transition-colors duration-500 hover:text-white sm:inline-flex"
          >
            <span className="absolute inset-0 -translate-x-full bg-[#047857] transition-transform duration-500 ease-out group-hover:translate-x-0" />
            <span className="relative flex items-center gap-2">
              Demander un devis
              <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className="p-2 text-[#F4F4F0] lg:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Ouvrir le menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-black/80 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="space-y-1 px-5 py-6">
              <Link to="/" data-testid="mobile-nav-accueil" className="block py-2.5 font-serif text-xl text-[#F4F4F0]">Accueil</Link>
              <p className="pt-3 text-[10px] uppercase tracking-[0.3em] text-[#2FBF8F]">Nos solutions</p>
              {SERVICES.map((s) => (
                <Link key={s.slug} to={`/${s.slug}`} data-testid={`mobile-nav-${s.slug}`} className="block py-2 pl-4 text-[#F4F4F0]/85">
                  {s.name}
                </Link>
              ))}
              <div className="pt-3">
                <Link to="/realisations" data-testid="mobile-nav-realisations" className="block py-2.5 font-serif text-xl text-[#F4F4F0]">Réalisations</Link>
                <Link to="/zone-intervention" data-testid="mobile-nav-zone" className="block py-2.5 font-serif text-xl text-[#F4F4F0]">Zone d'intervention</Link>
                <Link to="/blog" data-testid="mobile-nav-blog" className="block py-2.5 font-serif text-xl text-[#F4F4F0]">Blog</Link>
                <Link to="/a-propos" data-testid="mobile-nav-apropos" className="block py-2.5 font-serif text-xl text-[#F4F4F0]">À propos</Link>
                <Link to="/contact" data-testid="mobile-nav-contact" className="block py-2.5 font-serif text-xl text-[#F4F4F0]">Contact</Link>
              </div>
              <a
                href="tel:+33783693101"
                data-testid="mobile-nav-phone"
                className="mt-4 flex items-center justify-center gap-2.5 border border-[#047857] px-6 py-4 text-xs font-medium uppercase tracking-[0.2em] text-[#2FBF8F]"
              >
                <Phone size={14} /> 07 83 69 31 01
              </a>
              <Link
                to="/devis"
                data-testid="mobile-nav-devis-cta"
                className="mt-4 flex items-center justify-center gap-2 bg-[#047857] px-6 py-4 text-xs font-medium uppercase tracking-[0.2em] text-white"
              >
                Demander un devis <ArrowRight size={14} />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
